import { useTranslation } from "react-i18next";
import type { AnalysisCategory, AppAnalysisHealth } from "@repo/shared";

import { isCategoryScored } from "@/lib/health-format";

import { KpiCell, KpiStrip } from "./kpi";

const CATEGORY_ORDER: AnalysisCategory[] = [
  "memory",
  "expressions",
  "usage-hygiene",
  "model-complexity",
  "object-complexity",
];

function scoreTextColor(score: number): string {
  if (score >= 90) return "text-status-success-foreground";
  if (score >= 70) return "text-status-warning-foreground";
  return "text-status-danger-foreground";
}

interface HealthCategoriesGridProps {
  health: AppAnalysisHealth;
}

export function HealthCategoriesGrid({ health }: HealthCategoriesGridProps) {
  const { t } = useTranslation("analyses");

  const countByCategory = new Map<AnalysisCategory, number>();
  for (const finding of health.findings) {
    countByCategory.set(finding.category, (countByCategory.get(finding.category) ?? 0) + 1);
  }

  const byId = new Map(health.categories.map((c) => [c.id, c]));
  const orderedCategories = CATEGORY_ORDER.map(
    (id) => byId.get(id) ?? { id, score: 100, findingIds: [] },
  );

  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="text-[11px] font-bold tracking-[0.6px] text-faint uppercase">
        {t("categories.title")}
      </h2>
      <KpiStrip>
        {orderedCategories.map((category) => {
          const label = t(`category.${category.id}`);
          // A category the payload did not score keeps its slot, dimmed.
          if (!isCategoryScored(health, category.id)) {
            return (
              <KpiCell
                key={category.id}
                label={label}
                value="—"
                valueClassName="text-dim"
                subMeta={t("categories.notScored")}
                className="opacity-60"
              />
            );
          }
          const findingsCount = countByCategory.get(category.id) ?? 0;
          return (
            <KpiCell
              key={category.id}
              label={label}
              value={category.score}
              valueClassName={scoreTextColor(category.score)}
              subMeta={
                findingsCount === 0
                  ? t("categories.noIssues")
                  : t("categories.findingsCount", { count: findingsCount })
              }
            />
          );
        })}
      </KpiStrip>
    </section>
  );
}
